import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes } from 'react';
import { cn } from '@shared/lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Message d'erreur — typiquement fourni par react-hook-form (fieldState.error). */
  error?: string;
  /** Indication non bloquante, par exemple lorsqu'une saisie manuelle est requise. */
  warning?: string;
  /**
   * Position du label. 'top' (défaut) : label au-dessus, comportement habituel.
   * 'inline' : label compact à gauche du champ, en petit texte muted — utile
   * pour les filtres groupés (ex. plage de dates) où un placeholder empilé
   * prendrait trop de place.
   */
  labelPosition?: 'top' | 'inline';
}

/**
 * Primitive de champ de saisie, compatible react-hook-form
 * (transmission de la ref et des props natives via `{...register('champ')}`).
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, warning, id, className, labelPosition = 'top', ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const inputElement = (
    <input
      ref={ref}
      id={inputId}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${inputId}-error` : warning ? `${inputId}-warning` : undefined}
      className={cn(
        'h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm',
        'placeholder:text-slate-400 focus:border-emerald-500 focus:outline-2 focus:outline-emerald-600',
        error && 'border-red-500 focus:border-red-500 focus:outline-red-600',
        warning && !error && 'border-amber-400 bg-amber-50/40',
        labelPosition === 'inline' &&
          'h-8 rounded-md border-none bg-transparent px-1 shadow-none focus:outline-none',
        className,
      )}
      {...props}
    />
  );

  if (labelPosition === 'inline') {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          {label ? (
            <label htmlFor={inputId} className="shrink-0 text-xs font-medium text-slate-400">
              {label}
            </label>
          ) : null}
          {inputElement}
        </div>
        {error ? (
          <p id={`${inputId}-error`} role="alert" className="text-sm text-red-600">
            {error}
          </p>
        ) : warning ? (
          <p id={`${inputId}-warning`} className="text-xs text-amber-700">
            {warning}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label htmlFor={inputId} className="text-sm font-medium text-slate-700">
          {label}
        </label>
      ) : null}
      {inputElement}
      {error ? (
        <p id={`${inputId}-error`} role="alert" className="text-sm text-red-600">
          {error}
        </p>
      ) : warning ? (
        <p id={`${inputId}-warning`} className="text-xs text-amber-700">
          {warning}
        </p>
      ) : null}
    </div>
  );
});
